'use client';
// Registre des calibrations (§6.5).
//
// UNE SEULE SOURCE, deux points d'entrée. L'écran dédié montre tout le
// laboratoire ; l'onglet de la fiche d'équipement monte le MÊME composant avec
// `equipement` fixé. Rien n'est maintenu à deux endroits — deux écrans qui se
// ressemblent finissent toujours par diverger, et c'est le périmé qu'on lit.
//
// Trois lectures, parce qu'on n'y cherche pas la même chose :
//   · par PARAMÈTRE — la suite des facteurs dans le temps. Une dérive s'y voit
//     avant de devenir un rejet ; c'est la seule vue qui montre une tendance.
//   · JOURNAL — ce qui a été calibré récemment, tous paramètres mêlés.
//   · ÉCHÉANCES — ce qui est dû. Mais la périodicité n'est écrite que sur un
//     quart des lignes : la vue le DIT, au lieu de laisser croire que le reste
//     est à jour.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOngletAdresse } from '@/lib/useAdresse';
import { fetchEcriture } from '@/lib/ecrire';
import { BoutonJournal } from '@/components/BoutonJournal';
import { useGed, userName } from '@/lib/store';
import { CAL_RESULTATS, CAL_TYPES, CAL_CAUSES, CAL_FACTEUR_BORNES, vocabMeta, fmtDate , equipementsDesignables } from '@/lib/meta';
import { useRemonteEnHaut, I, Modale, card, inputStyle, EmptyState, FieldError, useBusy, RefISO, Selecteur, ChampRecherche, usePagination, Pagination } from '@/components/ui';
import { FormulaireLigne } from '@/components/RegistreEdition';
import { OngletsDossier, PanneauDossier } from '@/components/Dossier';
import { lbl, pill, H1 } from './commun';

import { Bouton } from '@/shared/Bouton';
import { peutGererParc } from '@/lib/droits-parc';
type Resume = {
  cle: string; equipementCode: string; analyteLibelle: string; analyteCode: string;
  nb: number; nbNonValide: number; nbSansResultat: number;
  derniereDate: string; dernierFacteur: number | null; periodicite: string;
  nbFacteurs: number; facteurMin: number | null; facteurMax: number | null;
  echeance: string | null; retardJours: number | null; moisDepuis: number | null;
};
type Ligne = {
  documentId: string; ref: string; equipementCode: string; analyteLibelle: string;
  date: string; heure: string; facteur: number | null; typeCalibration: string;
  resultat: string; cause: string; personnel: string; lotReactif: string;
  peremptionReactif: string; codeBoxystock: string; calibrant: string;
  lotCalibrant: string; periodicite: string; remarque: string;
  ncRef: string | null; importe: boolean;
};

type Vue = 'parametre' | 'journal' | 'echeances';
/* Les valeurs ADMISES dans l'adresse. Elle existe pour refuser
   `?onglet=nimportequoi` : sans elle, une adresse tronquée par un logiciel de
   messagerie ouvrirait un onglet vide au lieu du premier. */
const ONGLETS_VUE: readonly Vue[] = ['parametre', 'journal', 'echeances'];

async function lire(chemin: string) {
  const r = await fetch(`/api/ged/ged/calibrations${chemin}`, { credentials: 'include' });
  if (!r.ok) throw new Error('Lecture impossible');
  return r.json();
}

/* ------------------------------------------------------------ la courbe */

/**
 * Suite des facteurs dans le temps.
 *
 * L'échelle est centrée sur **1**, valeur attendue d'un facteur de calibration
 * (la médiane observée sur les 625 facteurs repris vaut exactement 1,000).
 * Les bornes d'acceptabilité ne sont PAS tracées tant que le laboratoire ne les
 * a pas fixées : dessiner une limite qu'on aurait choisie soi-même ferait
 * paraître anormales des valeurs que personne n'a jugées telles.
 */
function CourbeFacteur({ lignes, selection, onPoint }: {
  lignes: Ligne[]; selection: Ligne | null; onPoint: (l: Ligne | null) => void;
}) {
  const pts = lignes.filter((l) => l.facteur !== null && l.facteur !== undefined);
  if (pts.length < 2) {
    return (
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
        {pts.length === 0
          ? 'Ce gabarit de registre ne porte pas de facteur — il n’y a rien à tracer.'
          : 'Un seul facteur relevé : une tendance demande au moins deux points.'}
      </p>
    );
  }
  const W = 900, H = 190, PADX = 44, PADY = 18;
  const vals = pts.map((p) => p.facteur as number);
  const lo = Math.min(...vals, 0.9), hi = Math.max(...vals, 1.1);
  const marge = (hi - lo) * 0.15 || 0.1;
  const bas = lo - marge, haut = hi + marge;
  const x = (i: number) => PADX + (i * (W - PADX - 16)) / Math.max(1, pts.length - 1);
  const y = (v: number) => H - PADY - ((v - bas) / (haut - bas)) * (H - 2 * PADY);

  const reperes = [bas, 1, haut].filter((v, i, a) => a.indexOf(v) === i);
  const bornes = [CAL_FACTEUR_BORNES.min, CAL_FACTEUR_BORNES.max].filter((v): v is number => v !== null);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img"
      aria-label={`Suite des facteurs de calibration — ${pts.length} points`}>
      {reperes.map((v) => (
        <g key={v}>
          <line x1={PADX} y1={y(v)} x2={W - 16} y2={y(v)} stroke={v === 1 ? 'var(--navy-800)' : 'var(--border-default)'}
            strokeWidth={v === 1 ? 1.3 : 1} strokeDasharray={v === 1 ? '' : '4 4'} opacity={0.55} />
          <text x={2} y={y(v) + 3.5} fontSize={9} fontFamily="var(--font-mono)" fill="var(--text-muted)">{v.toFixed(2)}</text>
        </g>
      ))}
      {bornes.map((v) => (
        <line key={`b${v}`} x1={PADX} y1={y(v)} x2={W - 16} y2={y(v)} stroke="var(--red-500)" strokeWidth={1} strokeDasharray="2 3" opacity={0.7} />
      ))}
      <polyline points={pts.map((p, i) => `${x(i)},${y(p.facteur as number)}`).join(' ')}
        fill="none" stroke="var(--color-primary)" strokeWidth={1.5} opacity={0.85} />
      {pts.map((p, i) => {
        const sel = selection?.documentId === p.documentId;
        // Une calibration non validée se voit sur la courbe : c'est le point
        // qu'on cherche quand on ouvre cet écran.
        const rouge = p.resultat === 'non_valide';
        return (
          <circle key={p.documentId} cx={x(i)} cy={y(p.facteur as number)} r={sel ? 5 : rouge ? 4 : 3}
            fill={rouge ? 'var(--red-500)' : 'var(--color-primary)'}
            stroke={sel ? 'var(--navy-800)' : 'var(--surface-card)'} strokeWidth={sel ? 2 : 1}
            style={{ cursor: 'pointer' }}
            onClick={() => onPoint(sel ? null : p)}>
            <title>{`${p.date} · facteur ${p.facteur}${rouge ? ' · NON VALIDÉE' : ''}`}</title>
          </circle>
        );
      })}
    </svg>
  );
}

/* ------------------------------------------------------------- une ligne */

function LigneCal({ l, onNC, peutDeclarer, peutParc, onCorriger, onAnnuler }: {
  l: Ligne; onNC: (l: Ligne) => void; peutDeclarer: boolean;
  peutParc?: boolean; onCorriger?: (l: Ligne) => void; onAnnuler?: (l: Ligne) => void;
}) {
  const { settings } = useGed();
  const res = l.resultat ? CAL_RESULTATS[l.resultat] : null;
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', padding: '7px 0', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5 }}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)', minWidth: 78 }}>{fmtDate(l.date)}</span>
      {l.heure && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{l.heure}</span>}
      <span style={{ fontWeight: 700, color: 'var(--text-strong)', minWidth: 70 }}>{l.analyteLibelle}</span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>{l.equipementCode}</span>
      {l.facteur !== null && l.facteur !== undefined && (
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>×{l.facteur}</span>
      )}
      {l.typeCalibration && (
        <span title={CAL_TYPES[l.typeCalibration]?.aide} style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', cursor: 'help' }}>
          {CAL_TYPES[l.typeCalibration]?.label || l.typeCalibration}
        </span>
      )}
      {res
        ? pill(vocabMeta(CAL_RESULTATS, l.resultat))
        : <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>résultat non renseigné</span>}
      <span style={{ flex: '1 1 140px', color: 'var(--text-body)' }}>{l.cause}{l.remarque ? ` — ${l.remarque}` : ''}</span>
      {/* Le champ porte une CLÉ de compte (`tech.naamoun`) depuis la reprise du
          18/08 : l'afficher brut donnerait un identifiant technique là où le
          lecteur attend un nom. `userName` traduit ; il rend la clé telle quelle
          si le compte a disparu, ce qui est préférable à une case vide — on
          voit alors qu'il y avait quelqu'un. */}
      {l.personnel && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{userName(settings, l.personnel)}</span>}
      {l.ncRef
        ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{l.ncRef}</span>
        : (l.resultat === 'non_valide' && peutDeclarer && (
          <Bouton taille="sm" onClick={() => onNC(l)}>Déclarer une NC</Bouton>
        ))}
      {/* Lot 6 (11/08/2026) — correction tracée et annulation motivée (RQ). */}
      {peutParc && onCorriger && (
        <Bouton taille="sm" onClick={() => onCorriger(l)} title="Corriger (motif obligatoire, différence au journal)" aria-label="Corriger la calibration">
          <I n="pen" s={12} />
        </Bouton>
      )}
      {peutParc && onAnnuler && !l.ncRef && (
        <Bouton taille="sm" onClick={() => onAnnuler(l)} title="Annuler (saisie en double ou sur le mauvais équipement) — rien n’est effacé" aria-label="Annuler la calibration">
          <I n="x" s={12} />
        </Bouton>
      )}
    </div>
  );
}

/** Annulation MOTIVÉE (lot 6) — pour une saisie en double ou sur le mauvais
 *  équipement. Rien n'est effacé : la ligne sort des lectures, reste en base
 *  et au journal. Une calibration citée par une NC ne s'annule pas. */
function AnnulerCalibration({ l, onClose, onDone }: { l: Ligne; onClose: () => void; onDone: () => void }) {
  const { smq } = useGed();
  const { run, estOccupe } = useBusy();
  const [motif, setMotif] = useState('');
  return (
    <Modale titre="Annuler la calibration" onClose={onClose}
      sousTitre={`${l.analyteLibelle} du ${fmtDate(l.date)} sur ${l.equipementCode} — rien n'est effacé : la ligne sort des lectures, elle reste en base et au journal.`}
      pied={(
        <>
          <Bouton taille="sm" onClick={onClose}>Annuler</Bouton>
          <Bouton variante="primaire" taille="sm" onClick={() => void run('ann', async () => {
            const ok = await smq(`ged/calibrations/${l.documentId}/annuler`, { motif }, 'Calibration annulée — tracée au journal.');
            if (ok) { onDone(); onClose(); }
          })} disabled={estOccupe() || !motif.trim()}>
            {estOccupe() ? 'En cours…' : 'Annuler la calibration'}
          </Bouton>
        </>
      )}>
      <label style={{ display: 'grid', gap: 5, fontSize: 12.5, fontWeight: 600, color: 'var(--text-body)' }}>
        <span>Motif d’annulation <span style={{ color: 'var(--red-600)' }}>*</span></span>
        <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} autoFocus
          style={{ ...inputStyle, height: 'auto', padding: '9px 12px', fontWeight: 400, resize: 'vertical', fontFamily: 'var(--font-sans)' }} />
      </label>
    </Modale>
  );
}

/* --------------------------------------------------------------- l'écran */

export function Calibrations({ equipement }: { equipement?: string }) {
  const { session, refresh, showToast, equips, go } = useGed();
  /* L'onglet vit dans l'ADRESSE (relevé P0.8) : il se partage par lien, F5 y
     revient, et « Précédent » ramène à l'onglet précédent au lieu de quitter
     l'écran. */
  const [vue, setVue] = useOngletAdresse<Vue>('parametre', ONGLETS_VUE);
  // Un sous-onglet = une arrivée : on remonte (règle du 24/08/2026, cf. le crochet).
  useRemonteEnHaut(equipement ? null : vue);
  const [resume, setResume] = useState<Resume[]>([]);
  const [total, setTotal] = useState(0);
  const [choisi, setChoisi] = useState<Resume | null>(null);
  const [serie, setSerie] = useState<Ligne[]>([]);
  const [journal, setJournal] = useState<Ligne[]>([]);
  const [point, setPoint] = useState<Ligne | null>(null);
  const [correction, setCorrection] = useState<Ligne | null>(null);
  const [annulation, setAnnulation] = useState<Ligne | null>(null);
  const [q, setQ] = useState('');
  const [saisie, setSaisie] = useState(false);
  const [chargement, setChargement] = useState(true);
  const peutDeclarer = session.roles.length > 0;
  const peutParc = peutGererParc(session.roles);
  const suffixe = equipement ? `?equipement=${encodeURIComponent(equipement)}` : '';

  // Pas de `setChargement(true)` en tête : appelée depuis un effet, elle
  // poserait un état AVANT le premier await, ce que React refuse (rendus en
  // cascade). L'état part donc à `true` et ne retombe qu'une fois servi ; un
  // rechargement ultérieur garde les données à l'écran au lieu de les vider,
  // ce qui est de toute façon préférable.
  const charger = useCallback(async () => {
    try {
      const d = await lire(`/resume${suffixe}`);
      setResume(d.resume || []);
      setTotal(d.total || 0);
    } catch { setResume([]); } finally { setChargement(false); }
  }, [suffixe]);

  // Le chargement initial passe par `.then()` et non par `await charger()` :
  // appelée directement dans un effet, une fonction `async` empêche de voir que
  // ses `setState` sont bien postérieurs au premier await. Même forme que
  // l'effet du journal juste dessous. `charger` reste utilisée par les gestes
  // (après une saisie, après une déclaration de NC).
  useEffect(() => {
    let vivant = true;
    void lire(`/resume${suffixe}`)
      .then((d) => { if (vivant) { setResume(d.resume || []); setTotal(d.total || 0); } })
      .catch(() => { if (vivant) setResume([]); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [suffixe]);

  /* L6 (10/10/2026) : le journal (941 calibrations, 400 servies) se PAGINE, et
     un échec de lecture se DIT au lieu de se déguiser en journal vide (règle 8). */
  const [erreurJournal, setErreurJournal] = useState('');
  const [tourJournal, setTourJournal] = useState(0);
  useEffect(() => {
    if (vue !== 'journal') return;
    void lire(`/journal${suffixe}`)
      .then((d) => { setErreurJournal(''); setJournal(d.lignes || []); })
      .catch(() => setErreurJournal('Le journal des calibrations n’a pas pu être lu : le serveur n’a pas répondu.'));
  }, [vue, suffixe, tourJournal]);
  const pagesJournal = usePagination(journal);

  // La série suit le paramètre choisi. Aucun `setState` SYNCHRONE ici : React
  // le refuse (rendus en cascade). Le vidage passe donc par le choix lui-même
  // — `choisirParametre` — et l'affichage se garde par `choisi`, plus bas.
  useEffect(() => {
    if (!choisi) return;
    let vivant = true;
    void lire(`/serie?equipement=${encodeURIComponent(choisi.equipementCode)}&analyte=${encodeURIComponent(choisi.analyteLibelle)}`)
      // Une réponse arrivée après un autre choix écraserait la bonne série :
      // on n'accepte que celle du paramètre encore sélectionné.
      .then((d) => { if (vivant) setSerie(d.lignes || []); })
      .catch(() => { if (vivant) setSerie([]); });
    return () => { vivant = false; };
  }, [choisi]);

  /** Choisir un paramètre : on vide la série et le point AVANT de recharger.
   *  Le faire ici plutôt que dans un effet évite les rendus en cascade, et
   *  surtout évite d'afficher un instant la courbe du paramètre précédent. */
  const choisirParametre = (r: Resume | null) => {
    setPoint(null);
    setSerie([]);
    setChoisi(r);
  };

  const declarerNC = async (l: Ligne) => {
    try {
      const r = await fetchEcriture(`/api/ged/ged/calibrations/${l.documentId}/nc`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.error?.message || 'Déclaration refusée');
      showToast(`Non-conformité ${d.nc?.ref} ouverte.`);
      await charger();
      if (choisi) choisirParametre({ ...choisi });
      await refresh();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Déclaration refusée', 'erreur');
    }
  };

  // Le registre des calibrations ne porte que le CODE de l'appareil : on
  // rappelle sa désignation depuis le parc pour pouvoir le chercher et le lire
  // par son nom (« Erba Lyte Pro ») autant que par son code (« EQ03 »). Rien
  // n'est écrit en base — c'est une lecture du parc déjà chargé.
  const nomEquip = useCallback(
    (code: string) => equips.find((x) => x.code === code)?.nom || '',
    [equips],
  );

  const filtre = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return qq ? resume.filter((r) => `${r.equipementCode} ${nomEquip(r.equipementCode)} ${r.analyteLibelle}`.toLowerCase().includes(qq)) : resume;
  }, [resume, q, nomEquip]);

  const nonValides = resume.reduce((n, r) => n + r.nbNonValide, 0);
  const sansPeriodicite = resume.filter((r) => !r.echeance).length;
  const enRetard = resume.filter((r) => r.retardJours !== null && r.retardJours > 0);

  const corps = (
    <div style={{ display: 'grid', gap: 16 }}>
      <OngletsDossier id="cal" actif={vue} onChange={setVue} items={[
        { k: 'parametre', label: 'Par paramètre', n: resume.length },
        { k: 'journal', label: 'Journal', n: total },
        { k: 'echeances', label: 'Échéances', n: enRetard.length },
      ]} />

      <PanneauDossier id="cal" actif={vue}>
        {chargement && <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Chargement…</p>}

        {!chargement && vue === 'parametre' && (
          <div className="ged-2col-wide ged-colonnes-defilantes" style={{ display: 'grid', gridTemplateColumns: '340px minmax(0, 1fr)', gap: 18, alignItems: 'start' }}>
            <div>
              <ChampRecherche valeur={q} onChange={setQ} placeholder="Rechercher un paramètre…" style={{ ...inputStyle, marginBottom: 10 }} />
              {/* Plus de plafond de 620 px sur la seule liste : la colonne
                  entière défile désormais (`ged-colonnes-defilantes`), et deux
                  zones imbriquées donneraient deux barres de défilement l'une
                  dans l'autre — celle du dedans bloquant celle du dehors. */}
              <div style={{ ...card, overflow: 'hidden' }}>
                {filtre.map((r) => (
                  <button key={r.cle} onClick={() => choisirParametre(r)} className="ged-row"
                    style={{ display: 'block', width: '100%', textAlign: 'start', border: 'none', borderTop: '1px solid var(--border-subtle)', padding: '10px 13px', cursor: 'pointer', fontFamily: 'var(--font-sans)', background: choisi?.cle === r.cle ? 'var(--cyan-50)' : 'none' }}>
                    <div style={{ display: 'flex', gap: 7, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>{r.analyteLibelle}</span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>{r.equipementCode}</span>
                      {/* La liste est une colonne de 340 px : le nom de
                          l'appareil y tient sur la ligne suivante plutôt que
                          dans une seconde colonne, mais il est bien là — un
                          code seul ne se reconnaît pas. */}
                      {nomEquip(r.equipementCode) && (
                        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{nomEquip(r.equipementCode)}</span>
                      )}
                      {r.nbNonValide > 0 && (
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--red-600)' }}>{r.nbNonValide} non validée{r.nbNonValide > 1 ? 's' : ''}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
                      {r.nb} calibration{r.nb > 1 ? 's' : ''} · dernière {fmtDate(r.derniereDate)}
                      {r.dernierFacteur !== null && <> · ×{r.dernierFacteur}</>}
                    </div>
                  </button>
                ))}
                {filtre.length === 0 && <EmptyState titre="Aucun paramètre ne correspond." />}
              </div>
            </div>

            {choisi ? (
              <div style={{ ...card, padding: 20, display: 'grid', gap: 14 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <h2 style={{ margin: 0, fontSize: 16.5, fontWeight: 700, color: 'var(--text-strong)' }}>{choisi.analyteLibelle}</h2>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--color-primary-active)' }}>{choisi.equipementCode}</span>
                  {/* Les calibrations s'écrivent au journal CHAÎNÉ de leur
                      équipement (saisie, non-conformité, correction,
                      annulation) : le voici, accessible d'ici (24/08/2026). */}
                  <BoutonJournal reference={choisi.equipementCode} quoi="de cet équipement — calibrations comprises" />
                  {nomEquip(choisi.equipementCode) && (
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{nomEquip(choisi.equipementCode)}</span>
                  )}
                  <span style={{ flex: 1 }} />
                  {choisi.periodicite && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{choisi.periodicite}</span>}
                </div>
                {!choisi.analyteCode && (
                  <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
                    Ce libellé n’a pas de code au référentiel des analytes : ses calibrations
                    ne sont rattachées à aucun paramètre.
                  </p>
                )}
                <CourbeFacteur lignes={serie} selection={point} onPoint={setPoint} />
                {CAL_FACTEUR_BORNES.min === null && CAL_FACTEUR_BORNES.max === null && serie.some((l) => l.facteur !== null) && (
                  <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
                    Aucune borne d’acceptabilité n’est tracée : le laboratoire ne les a pas
                    fixées. L’écran montre la suite, il ne juge pas.
                  </p>
                )}
                <div>
                  {serie.slice().reverse().map((l) => (
                    <LigneCal key={l.documentId} l={l} onNC={declarerNC} peutDeclarer={peutDeclarer} peutParc={peutParc} onCorriger={setCorrection} onAnnuler={setAnnulation} />
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ ...card, padding: '40px 24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13.5 }}>
                Choisissez un paramètre pour voir la suite de ses facteurs.
              </div>
            )}
          </div>
        )}

        {!chargement && vue === 'journal' && (
          <div style={{ ...card, padding: 16 }}>
            <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--text-muted)' }}>
              Les 400 dernières calibrations sur {total}.
            </p>
            {erreurJournal && (
              <EmptyState titre="Journal indisponible" indice={erreurJournal}
                action={<Bouton taille="sm" onClick={() => setTourJournal((t) => t + 1)}>Réessayer</Bouton>} />
            )}
            {!erreurJournal && pagesJournal.visibles.map((l) => <LigneCal key={l.documentId} l={l} onNC={declarerNC} peutDeclarer={peutDeclarer} peutParc={peutParc} onCorriger={setCorrection} onAnnuler={setAnnulation} />)}
            {!erreurJournal && (
              <Pagination page={pagesJournal.page} pages={pagesJournal.pages} debut={pagesJournal.debut} fin={pagesJournal.fin}
                total={pagesJournal.total} onPage={pagesJournal.setPage} quoi="calibrations" />
            )}
          </div>
        )}

        {correction && (
          <FormulaireLigne chemin="ged/calibrations" suffixe="/corriger"
            titreCorrection="Corriger la calibration (tracé au journal)" titreCreation=""
            champs={[
              { k: 'motif', label: 'Motif de la correction', requis: true, type: 'long' },
              { k: 'date', label: 'Date' },
              { k: 'heure', label: 'Heure' },
              { k: 'facteur', label: 'Facteur' },
              { k: 'resultat', label: 'Résultat', type: 'options', options: [['valide', 'Validée'], ['non_valide', 'Non validée']] },
              { k: 'cause', label: 'Cause' },
              { k: 'personnel', label: 'Personnel' },
              { k: 'lotReactif', label: 'Lot de réactif' },
              { k: 'lotCalibrant', label: 'Lot de calibrant' },
              { k: 'remarque', label: 'Remarque', type: 'long' },
            ]}
            ligne={{ ...correction, motif: '' } as unknown as Record<string, unknown>}
            complementPayload={() => ({})}
            onClose={() => setCorrection(null)}
            onDone={() => void charger()} />
        )}
        {annulation && (
          <AnnulerCalibration l={annulation} onClose={() => setAnnulation(null)} onDone={() => void charger()} />
        )}

        {!chargement && vue === 'echeances' && (
          <div style={{ ...card, padding: 16, display: 'grid', gap: 10 }}>
            {/* Le point capital de cette vue : ne pas laisser croire que ce qui
                n'a pas de périodicité est à jour. */}
            {sansPeriodicite > 0 && (
              <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5 }}>
                <strong style={{ color: 'var(--text-strong)' }}>{sansPeriodicite} paramètre{sansPeriodicite > 1 ? 's' : ''} sans périodicité</strong>{' '}
                — leur échéance ne peut pas être calculée. Ils ne sont pas « à jour », ils sont
                hors de portée de cette vue.
              </div>
            )}
            {enRetard.length === 0 && <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>Aucune échéance dépassée parmi les paramètres dont la périodicité est connue.</p>}
            {enRetard.sort((a, b) => (b.retardJours || 0) - (a.retardJours || 0)).map((r) => (
              <button key={r.cle} onClick={() => { choisirParametre(r); setVue('parametre'); }} className="ged-row"
                style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', width: '100%', textAlign: 'start', background: 'none', border: 'none', borderTop: '1px solid var(--border-subtle)', padding: '8px 0', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12.5 }}>
                <span style={{ fontWeight: 700, color: 'var(--text-strong)', minWidth: 80 }}>{r.analyteLibelle}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>{r.equipementCode}</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{nomEquip(r.equipementCode)}</span>
                <span style={{ color: 'var(--text-body)' }}>{r.periodicite}</span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>dernière {fmtDate(r.derniereDate)}</span>
                <span style={{ fontWeight: 700, color: 'var(--red-600)' }}>+{r.retardJours} j</span>
              </button>
            ))}
          </div>
        )}
      </PanneauDossier>
    </div>
  );

  // Monté dans la fiche d'équipement : pas de titre ni de bandeau, ils y
  // feraient doublon.
  if (equipement) {
    return (
      <div style={{ display: 'grid', gap: 14 }}>
        {/* LECTURE SEULE DEPUIS L'APPAREIL (Ines, 29/10/2026) : « saisir une
            calibration à partir d'un équipement me semble anecdotique […] ça
            doit être géré dans la cal ». On lit ici toutes les calibrations de
            l'appareil, tous paramètres confondus ; la saisie se fait à l'écran
            Calibrations. */}
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Toutes les calibrations de cet appareil, tous paramètres confondus. La saisie se fait depuis
          l’écran <Bouton taille="sm" variante="discret" onClick={() => go('cal')}>Calibrations →</Bouton>
        </p>
        {corps}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <H1 eyebrow={<>Ressources · <RefISO code="6.5" /></>} title="Calibrations" />
        {peutDeclarer && (
          <Bouton variante="primaire" onClick={() => setSaisie(!saisie)}>
            <I n="plus" s={15} />Saisir une calibration
          </Bouton>
        )}
      </div>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
        {total} calibration{total > 1 ? 's' : ''} · {resume.length} paramètre{resume.length > 1 ? 's' : ''} suivi{resume.length > 1 ? 's' : ''}
        {nonValides > 0 && <> · <strong style={{ color: 'var(--red-600)' }}>{nonValides} non validée{nonValides > 1 ? 's' : ''}</strong></>}
      </p>
      {saisie && <FormulaireCal onFait={() => { setSaisie(false); void charger(); }} />}
      {corps}
    </div>
  );
}

/**
 * Le PARAMÈTRE calibré — proposé, pas imposé.
 *
 * Le champ était une saisie libre : il fallait connaître le libellé exact et le
 * retaper, faute de quoi la même mesure se retrouvait sous « FT4 », « FT 4 » et
 * « T4 libre » — trois séries qui ne se rejoignent jamais dans les courbes.
 * Demande de la RQ du 18/08 : proposer, à partir de la liaison
 * paramètre-équipement, une liste où l'on peut chercher.
 *
 * ── Pourquoi la saisie reste LIBRE ─────────────────────────────────────────
 *
 * Fermer la liste bloquerait la première calibration d'un paramètre sur un
 * appareil neuf — il n'est alors dans aucune des trois sources du serveur. On
 * propose donc, et on laisse écrire ; l'écran DIT quand la valeur saisie ne
 * figure pas dans les propositions, ce qui suffit à faire remarquer une faute
 * de frappe sans l'interdire.
 */
function ChoixParametre({ equipement, valeur, onChange }: {
  equipement: string; valeur: string; onChange: (v: string) => void;
}) {
  const [liste, setListe] = useState<{ libelle: string; origines: string[] }[]>([]);
  const [etat, setEtat] = useState<'vide' | 'charge' | 'pret' | 'echec'>('vide');
  const [ouvert, setOuvert] = useState(false);

  useEffect(() => {
  /* ⚠️ FAUX POSITIF ASSUMÉ de `react-hooks/set-state-in-effect` (02/09/2026).
     Le `setState` synchrone ici n'ajuste pas un état à une entrée : il EFFACE
     la donnée précédente avant d'aller en chercher une autre. Sans lui, l'écran
     afficherait les données de la fiche PRÉCÉDENTE pendant tout le temps de la
     requête — un faux vide à l'envers, qui affirme sur la nouvelle fiche ce qui
     était vrai de l'ancienne. C'est le seul cas où l'on veut ce rendu
     intermédiaire, et il ne se dérive pas : la donnée vient du réseau.
     ⚠️ Silence CIBLÉ, ligne à ligne. */
  // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!equipement) { setListe([]); setEtat('vide'); return undefined; }
    let vivant = true;
    setEtat('charge');
    void fetch(`/api/ged/ged/calibrations/parametres?equipement=${encodeURIComponent(equipement)}`,
      { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => { if (vivant) { setListe(d?.parametres || []); setEtat('pret'); } })
      // Un échec ne doit pas bloquer la saisie : on retombe sur le champ libre,
      // et on le DIT — une liste vide sans explication se lit « aucun paramètre
      // n'est calibrable ici », ce qui serait faux.
      .catch(() => { if (vivant) { setListe([]); setEtat('echec'); } });
    return () => { vivant = false; };
  }, [equipement]);

  const q = valeur.trim().toLowerCase();
  const propositions = q
    ? liste.filter((p) => p.libelle.toLowerCase().includes(q))
    : liste;
  const connu = !q || liste.some((p) => p.libelle.toLowerCase() === q);

  return (
    <div style={{ position: 'relative' }}>
      <label style={lbl} htmlFor="cal-an">Paramètre <span style={{ color: 'var(--red-600)' }}>*</span></label>
      <input
        id="cal-an" value={valeur} autoComplete="off"
        onChange={(e) => { onChange(e.target.value); setOuvert(true); }}
        onFocus={() => setOuvert(true)}
        // Le clic sur une proposition doit être TRAITÉ avant la fermeture :
        // un `onBlur` immédiat volerait l'événement à la liste.
        onBlur={() => window.setTimeout(() => setOuvert(false), 150)}
        placeholder={equipement ? 'Chercher ou saisir…' : 'Choisissez d’abord l’équipement'}
        style={inputStyle} />
      {equipement && etat === 'pret' && !connu && (
        <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--amber-700)' }}>
          Ce paramètre n’est pas dans la liste de cet appareil — vérifiez l’orthographe, ou continuez
          s’il s’agit d’une première calibration.
        </p>
      )}
      {etat === 'echec' && (
        <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
          Liste des paramètres injoignable — la saisie reste possible.
        </p>
      )}
      {ouvert && equipement && propositions.length > 0 && (
        <ul role="listbox" aria-label="Paramètres de cet équipement"
          style={{
            position: 'absolute', insetInlineStart: 0, insetInlineEnd: 0, top: '100%', zIndex: 40,
            margin: '2px 0 0', padding: 4, listStyle: 'none', maxHeight: 220, overflowY: 'auto',
            background: 'var(--surface-card)', border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)',
          }}>
          {propositions.slice(0, 40).map((p) => (
            <li key={p.libelle}>
              <button type="button" role="option" aria-selected={p.libelle.toLowerCase() === q}
                onMouseDown={() => { onChange(p.libelle); setOuvert(false); }}
                style={{
                  display: 'flex', alignItems: 'baseline', gap: 8, width: '100%', textAlign: 'start',
                  background: 'none', border: 'none', padding: '6px 8px', cursor: 'pointer',
                  fontFamily: 'var(--font-sans)', fontSize: 12.5, borderRadius: 'var(--radius-sm)',
                }}
                className="ged-row">
                <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{p.libelle}</span>
                {/* D'où vient la proposition : la technique fait autorité, le
                    CQI atteste la mesure, l'historique dit seulement « déjà vu
                    ici ». Les confondre ferait prendre une habitude pour une
                    déclaration. */}
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  {p.origines.map((o) => (o === 'technique' ? 'technique' : o === 'cqi' ? 'CQI' : 'déjà calibré')).join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ la saisie */

function FormulaireCal({ equipement, onFait }: { equipement?: string; onFait: () => void }) {
  const { equips, showToast } = useGed();
  const { run, estOccupe } = useBusy();
  const vide = {
    equipementCode: equipement || '', analyteLibelle: '', date: '', heure: '',
    facteur: '', typeCalibration: 'LOT', resultat: 'valide', cause: '',
    lotReactif: '', peremptionReactif: '', codeBoxystock: '',
    calibrant: '', lotCalibrant: '', periodicite: '', remarque: '',
  };
  const [f, setF] = useState(vide);
  const [erreur, setErreur] = useState('');

  const enregistrer = () => run('creer', async () => {
    if (!f.equipementCode) { setErreur('Choisissez l’équipement.'); return; }
    if (!f.analyteLibelle.trim()) { setErreur('Indiquez le paramètre calibré.'); return; }
    setErreur('');
    try {
      const r = await fetchEcriture('/api/ged/ged/calibrations', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.error?.message || 'Enregistrement refusé');
      showToast(`Calibration ${d.calibration?.ref} enregistrée.`);
      setF(vide);
      onFait();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Enregistrement refusé', 'erreur');
    }
  });

  const G: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px 16px' };
  return (
    <div style={{ ...card, padding: 16, display: 'grid', gap: 10 }}>
      <div style={G}>
        {!equipement && (
          <div>
            <label style={lbl} htmlFor="cal-eq">Équipement <span style={{ color: 'var(--red-600)' }}>*</span></label>
            <Selecteur id="cal-eq" value={f.equipementCode} onChange={(v) => setF({ ...f, equipementCode: v })} style={inputStyle}>
              <option value="">—</option>
              {/* Sans accessoires — un onduleur ne se calibre pas. */}
              {equipementsDesignables(equips).filter((x) => x.code).map((x) => <option key={x.code} value={x.code}>{x.code} — {x.nom}</option>)}
            </Selecteur>
          </div>
        )}
        <ChoixParametre
          equipement={f.equipementCode}
          valeur={f.analyteLibelle}
          onChange={(v) => { setF({ ...f, analyteLibelle: v }); if (erreur) setErreur(''); }} />
        <div><label style={lbl} htmlFor="cal-date">Date</label>
          <input id="cal-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="cal-heure">Heure</label>
          <input id="cal-heure" value={f.heure} onChange={(e) => setF({ ...f, heure: e.target.value })} placeholder="10h30" style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="cal-fact">Facteur</label>
          <input id="cal-fact" value={f.facteur} onChange={(e) => setF({ ...f, facteur: e.target.value })} placeholder="0,958" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
        <div><label style={lbl} htmlFor="cal-type">Type</label>
          <select id="cal-type" value={f.typeCalibration} onChange={(e) => setF({ ...f, typeCalibration: e.target.value })} style={inputStyle}>
            {Object.entries(CAL_TYPES).map(([k, v]) => <option key={k} value={k} title={v.aide}>{v.label}</option>)}
          </select></div>
        <div><label style={lbl} htmlFor="cal-res">Résultat</label>
          <select id="cal-res" value={f.resultat} onChange={(e) => setF({ ...f, resultat: e.target.value })} style={inputStyle}>
            {Object.entries(CAL_RESULTATS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select></div>
        <div><label style={lbl} htmlFor="cal-cause">Cause</label>
          <select id="cal-cause" value={f.cause} onChange={(e) => setF({ ...f, cause: e.target.value })} style={inputStyle}>
            <option value="">—</option>
            {CAL_CAUSES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select></div>
        <div><label style={lbl} htmlFor="cal-lot">Lot de réactif</label>
          <input id="cal-lot" value={f.lotReactif} onChange={(e) => setF({ ...f, lotReactif: e.target.value })} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
        <div><label style={lbl} htmlFor="cal-dp">Péremption du réactif</label>
          <input id="cal-dp" type="date" value={f.peremptionReactif} onChange={(e) => setF({ ...f, peremptionReactif: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="cal-boxy">Code boxystock</label>
          <input id="cal-boxy" value={f.codeBoxystock} onChange={(e) => setF({ ...f, codeBoxystock: e.target.value })} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
        <div><label style={lbl} htmlFor="cal-cal">Calibrant</label>
          <input id="cal-cal" value={f.calibrant} onChange={(e) => setF({ ...f, calibrant: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="cal-lotcal">Lot du calibrant</label>
          <input id="cal-lotcal" value={f.lotCalibrant} onChange={(e) => setF({ ...f, lotCalibrant: e.target.value })} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="cal-rem">Remarque</label>
          <input id="cal-rem" value={f.remarque} onChange={(e) => setF({ ...f, remarque: e.target.value })} style={inputStyle} /></div>
      </div>
      <FieldError>{erreur}</FieldError>
      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        Une calibration non validée n’ouvre pas de non-conformité toute seule : refaite dans
        la foulée, c’est une routine. Le bouton est sur la ligne, une fois enregistrée.
      </span>
      <Bouton variante="primaire" taille="sm" onClick={() => void enregistrer()} disabled={estOccupe()} style={{ width: 'fit-content' }}>
        <I n="check" s={13} />{estOccupe('creer') ? 'Enregistrement…' : 'Enregistrer la calibration'}
      </Bouton>
    </div>
  );
}
